import { useTu } from "@src/appV2/i18n/useTu";
import { Box, Button } from "@mui/material";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { Text } from "@src/appV2/redesign/components/Text";
import { TextInput } from "@src/appV2/redesign/components/TextInput";
import { type FormEvent, useState } from "react";

const OTP_CODE_LENGTH = 8;

interface OtpEntryFormProps {
  email: string;
  loading: boolean;
  errorMessage?: string;
  /** Whether the resend cooldown is currently active. */
  isResendCoolingDown: boolean;
  /** Seconds left on the resend cooldown; shown in the button label. */
  resendSecondsRemaining: number;
  onSubmit: (code: string) => Promise<void>;
  onResend: () => void;
  onBack: () => void;
}

/** Group the raw digits as `1234 5678` for display. */
function formatOtp(rawDigits: string): string {
  if (rawDigits.length <= 4) {
    return rawDigits;
  }
  return `${rawDigits.slice(0, 4)} ${rawDigits.slice(4)}`;
}

/**
 * Code-entry screen shown after the user requests an email OTP. The user types
 * the 8-digit code from the email here, on the same browser that requested it.
 * The input stores raw digits and displays them grouped as `1234 5678`.
 */
export function OtpEntryForm(props: OtpEntryFormProps): React.ReactElement {
  const { tu } = useTu("login");
  const {
    email,
    loading,
    errorMessage,
    isResendCoolingDown,
    resendSecondsRemaining,
    onSubmit,
    onResend,
    onBack,
  } = props;

  const [code, setCode] = useState("");

  const isCodeComplete = code.length === OTP_CODE_LENGTH;

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!isCodeComplete) {
      return;
    }
    await onSubmit(code);
  }

  return (
    <Box
      component="form"
      sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 5 }}
      onSubmit={(event) => {
        void handleSubmit(event);
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <Text sx={{ color: "primary.main", fontSize: "1.25rem" }}>✓</Text>
        <Text bold variant="h5" sx={{ color: "base.base" }}>
          {tu("Enter your code")}
        </Text>
      </Box>
      <Text variant="body1" sx={{ color: "base.muted", textAlign: "center" }}>
        {tu("We sent an 8-digit code to {{email}}. Enter it below to sign in.", {
          values: { email },
        })}
      </Text>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1, width: "100%" }}>
        <Text semibold variant="body2" sx={{ color: "base.muted" }}>
          {tu("Verification code")}
        </Text>
        <TextInput
          fullWidth
          required
          type="text"
          placeholder="0000 0000"
          value={formatOtp(code)}
          disabled={loading}
          autoComplete="one-time-code"
          inputProps={{
            "aria-label": tu("Verification code"),
            inputMode: "numeric",
            pattern: "[0-9 ]*",
            // 8 digits + the grouping space.
            maxLength: OTP_CODE_LENGTH + 1,
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              fontSize: "1.5rem",
              letterSpacing: "0.4rem",
              textAlign: "center",
              backgroundColor: "background.standout",
            },
          }}
          onChange={(event) => {
            setCode(event.target.value.replaceAll(/\D/g, "").slice(0, OTP_CODE_LENGTH));
          }}
        />
      </Box>
      {errorMessage && (
        <Text variant="body2" role="alert" sx={{ color: "error.main", textAlign: "center" }}>
          {errorMessage}
        </Text>
      )}
      <LoadingButton
        fullWidth
        type="submit"
        variant="primary"
        size="large"
        isLoading={loading}
        disabled={!isCodeComplete}
      >
        {tu("Sign in")}
      </LoadingButton>
      <Button
        variant="text"
        disabled={loading || isResendCoolingDown}
        sx={{ textTransform: "none", color: "primary.main" }}
        onClick={onResend}
      >
        {isResendCoolingDown
          ? tu("Resend code in {{seconds}}s", { values: { seconds: resendSecondsRemaining } })
          : tu("Resend code")}
      </Button>
      <Button
        variant="text"
        disabled={loading}
        sx={{ textTransform: "none", color: "primary.main" }}
        onClick={onBack}
      >
        {tu("Go back to login page")}
      </Button>
    </Box>
  );
}
